import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  ArrowRight,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { BannerEntity } from '../../../domain/entities/banner.entity';
import { BannerLayoutMode, BannerHeightSize } from '../../../domain/entities/banner-settings.entity';
import { Button } from './button';

interface BannerSliderProps {
  banners: BannerEntity[];
  className?: string;
  layoutMode?: BannerLayoutMode;
  heightSize?: BannerHeightSize;
  autoplaySpeed?: number;
}

export const BannerSlider: React.FC<BannerSliderProps> = ({
  banners,
  className = '',
  layoutMode = 'boxed',
  autoplaySpeed = 6500,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const navigate = useNavigate();

  const total = banners.length;

  useEffect(() => {
    if (!isPlaying || total <= 1) return;

    const interval = autoplaySpeed && autoplaySpeed >= 1000 ? autoplaySpeed : 6500;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % total);
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying, total, currentIndex, autoplaySpeed]);

  // Manejar reproducción de video cuando cambia el slide
  useEffect(() => {
    banners.forEach((banner, idx) => {
      const vid = videoRefs.current[banner.id];
      if (vid) {
        if (idx === currentIndex) {
          vid.currentTime = 0;
          vid.play().catch(() => {});
        } else {
          vid.pause();
        }
      }
    });
  }, [currentIndex, banners]);

  if (!banners || banners.length === 0) return null;

  const currentBanner = banners[currentIndex];

  // Determinar si el banner actual tiene contenido textual explícito (Editorial)
  const isEditorial =
    currentBanner.displayType === 'editorial' ||
    (currentBanner.displayType !== 'graphic' &&
      Boolean(
        currentBanner.title?.trim() ||
        (currentBanner.subtitle?.trim() && !currentBanner.subtitle.startsWith('{')) ||
        currentBanner.badgeText?.trim() ||
        currentBanner.secondaryCtaText?.trim()
      ));

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % total);
  };

  // Soporte para gestos táctiles Swipe en móvil
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 40; // Umbral mínimo de swipe en px

    if (diff > threshold) {
      handleNext();
    } else if (diff < -threshold) {
      handlePrev();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleSlideClick = (banner: BannerEntity) => {
    const destination = banner.ctaLink || banner.secondaryCtaLink;
    if (destination) {
      if (destination.startsWith('http')) {
        window.open(destination, '_blank', 'noopener,noreferrer');
      } else {
        navigate(destination);
      }
    }
  };

  // Clases condicionales según el modo de disposición
  const isFullWidth = layoutMode === 'full';
  const containerShapeClasses = isFullWidth
    ? 'rounded-none shadow-none border-0'
    : 'rounded-2xl shadow-xl border border-neutral-800/40';

  // Relación de aspecto estándar 4:1 (1600 × 400 px nativo). Si tiene imagen móvil se adapta en pantallas pequeñas.
  const aspectClasses = currentBanner?.mobileMediaUrl
    ? 'aspect-[16/9] sm:aspect-[4/1]'
    : 'aspect-[4/1]';

  return (
    <div
      className={`relative w-full overflow-hidden bg-neutral-950 text-white select-none group ${aspectClasses} ${containerShapeClasses} ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Contenedor de Medios (Imágenes y Videos) */}
      <div
        className={`relative w-full h-full flex items-center ${
          (currentBanner.ctaLink || currentBanner.secondaryCtaLink) ? 'cursor-pointer' : ''
        }`}
        onClick={() => handleSlideClick(currentBanner)}
      >
        {banners.map((banner, index) => {
          const isCurrent = index === currentIndex;

          return (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                isCurrent ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
            >
              {banner.mediaType === 'video' ? (
                <video
                  ref={(el) => { videoRefs.current[banner.id] = el; }}
                  src={banner.mediaUrl}
                  poster={banner.videoPosterUrl}
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover object-center"
                />
              ) : (
                <picture className="w-full h-full block">
                  {banner.mobileMediaUrl && (
                    <source media="(max-width: 639px)" srcSet={banner.mobileMediaUrl} />
                  )}
                  <img
                    src={banner.mediaUrl}
                    alt={banner.title || 'Banner Bartech'}
                    width={1920}
                    height={480}
                    decoding="async"
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    className={`w-full h-full ${
                      banner.imageFit === 'contain'
                        ? 'object-contain object-center'
                        : 'object-cover object-center'
                    }`}
                  />
                </picture>
              )}
            </div>
          );
        })}

        {/* Degradado Oscuro en Desktop: Solo si tiene contenido editorial y en hover */}
        {isEditorial && (
          <div className="hidden sm:block absolute inset-0 z-20 bg-gradient-to-tr from-neutral-950/90 via-neutral-950/50 to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        )}

        {/* Contenido Textual y Acciones del Banner Editorial (Oculto en móvil, visible en desktop) */}
        {isEditorial && (
          <div
            className={`hidden sm:flex absolute inset-0 z-30 pointer-events-none flex-col justify-end ${
              isFullWidth
                ? 'max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 sm:pb-8 lg:pb-10'
                : 'p-3 sm:p-8 lg:p-10'
            }`}
          >
            <div className="max-w-lg lg:max-w-xl transition-all duration-300 sm:opacity-0 sm:group-hover:opacity-100 sm:pointer-events-none sm:group-hover:pointer-events-auto pointer-events-auto">
              <div className="space-y-1.5 sm:space-y-3.5 text-left">
                {currentBanner.badgeText && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest bg-brand-primary text-white shadow-md">
                    <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span>{currentBanner.badgeText}</span>
                  </div>
                )}

                {currentBanner.title && (
                  <h2 className="text-lg sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight drop-shadow-lg">
                    {currentBanner.title}
                  </h2>
                )}

                {currentBanner.subtitle && !currentBanner.subtitle.startsWith('{') && (
                  <p className="text-[11px] sm:text-sm text-neutral-200 leading-relaxed drop-shadow-md max-w-md line-clamp-2">
                    {currentBanner.subtitle}
                  </p>
                )}

                {/* Botones del Banner */}
                {(currentBanner.ctaText || currentBanner.secondaryCtaText) && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 sm:gap-2.5">
                    {currentBanner.ctaText && (
                      currentBanner.ctaLink && currentBanner.ctaLink.startsWith('http') ? (
                        <a
                          href={currentBanner.ctaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-5 sm:py-2.5 rounded-xl bg-brand-primary hover:bg-brand-hover text-white font-bold text-xs sm:text-sm shadow-xl transition-all duration-150"
                        >
                          <span>{currentBanner.ctaText}</span>
                          <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                        </a>
                      ) : (
                        <Link to={currentBanner.ctaLink || '/catalogo'}>
                          <Button variant="primary" size="sm" className="h-8 px-3 text-xs sm:h-10 sm:px-5 sm:text-sm font-bold shadow-xl">
                            <span>{currentBanner.ctaText}</span>
                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                          </Button>
                        </Link>
                      )
                    )}

                    {currentBanner.secondaryCtaText && (
                      currentBanner.secondaryCtaLink && currentBanner.secondaryCtaLink.startsWith('http') ? (
                        <a
                          href={currentBanner.secondaryCtaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-5 sm:py-2.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-900 text-white font-medium text-xs sm:text-sm backdrop-blur-sm transition-all duration-150 border border-white/20 shadow-lg"
                        >
                          <span>{currentBanner.secondaryCtaText}</span>
                        </a>
                      ) : (
                        <Link to={currentBanner.secondaryCtaLink || '/contacto'}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 text-xs sm:h-10 sm:px-5 sm:text-sm border-white/30 bg-neutral-900/70 text-white hover:bg-neutral-900 shadow-lg"
                          >
                            <span>{currentBanner.secondaryCtaText}</span>
                          </Button>
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Barra de Controles Inferiores (Optimizada para Móvil y Desktop) */}
      <div
        className={`absolute bottom-2.5 sm:bottom-4 z-30 flex items-center gap-1.5 sm:gap-2 bg-neutral-950/75 backdrop-blur-md px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border border-white/10 shadow-lg ${
          isFullWidth ? 'right-2.5 sm:right-6 lg:right-10' : 'right-2.5 sm:right-6'
        }`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          aria-label="Slide anterior"
          className="p-1 sm:p-1.5 rounded-full text-neutral-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>

        {/* Indicadores de Slide */}
        <div className="flex items-center gap-1 px-0.5 sm:gap-1.5 sm:px-1">
          {banners.map((b, i) => (
            <button
              key={b.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(i);
              }}
              aria-label={`Ir al slide ${i + 1}`}
              className={`h-1 sm:h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === currentIndex ? 'w-4 sm:w-6 bg-brand-primary' : 'w-1 sm:w-1.5 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          aria-label="Slide siguiente"
          className="p-1 sm:p-1.5 rounded-full text-neutral-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>

        <span className="w-px h-2.5 sm:h-3.5 bg-neutral-700 mx-0.5" />

        {/* Botón Play / Pausa */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsPlaying((p) => !p);
          }}
          aria-label={isPlaying ? 'Pausar rotación' : 'Reanudar rotación'}
          className="p-1 sm:p-1.5 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          title={isPlaying ? 'Pausar reproducción automática' : 'Reanudar'}
        >
          {isPlaying ? (
            <Pause className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          ) : (
            <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          )}
        </button>
      </div>
    </div>
  );
};
